import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { protocolsApi } from "../api/protocols";
import { dailyCheckinApi } from "../api/dailyCheckin";
import { pickActiveProtocolId, protocolProgress } from "../utils/activeProtocol";
import {
  localDateKey,
  homeTimeState,
  eveningDoneToday,
  mergeEveningDone,
  eveningDoneFromServer,
  REVEAL_THRESHOLD,
  isRevealed,
  isSafetyGated,
  selectPrimaryCard,
  showsTodaysProtocolCard,
} from "../utils/homeCard";
import { ScoreRing } from "../components/ScoreRing";
import { SafetyGatedView } from "../components/SafetyGatedView";
import { BottomNav } from "../components/BottomNav";
import SignalInfoButton from "../components/SignalInfoButton";
import { presentedScore } from "../utils/presentedScore";
import RevealCountdown from "../components/RevealCountdown";
import ProtocolCard from "../components/ProtocolCard";
import PushNotificationPrompt from "../components/PushNotificationPrompt";
import ReportCard from "../components/ReportCard";
import DeepDiveCard from "../components/DeepDiveCard";
import { Wordmark } from "../components/Wordmark";

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .haven { font-family: 'DM Sans', sans-serif; background: ${PALETTE.bg}; min-height: 100vh; color: ${PALETTE.text}; max-width: 420px; margin: 0 auto; }
  .serif { font-family: 'Playfair Display', Georgia, serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fu  { animation: fadeUp 0.5s ease forwards; }
  .d1  { animation-delay: 0.05s; opacity: 0; }
  .d2  { animation-delay: 0.15s; opacity: 0; }
  .d3  { animation-delay: 0.25s; opacity: 0; }
  .d4  { animation-delay: 0.38s; opacity: 0; }

  /* DeepDiveCard's WhiteShell (locked/teaser states) expects this
     class from its former home on Profile — same definition, moved
     here so the light-state card keeps its border-radius/shadow. */
  .section-card {
    background: ${PALETTE.surface};
    border-radius: 20px;
    padding: 22px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.07);
    margin-bottom: 12px;
  }

  .lp-signal {
    width: 100%;
    text-align: left;
    background: ${PALETTE.surface};
    border: none;
    border-radius: 20px;
    padding: 18px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 18px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.07);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .lp-signal:hover { transform: translateY(-1px); box-shadow: 0 4px 22px rgba(28,24,20,0.10); }

  .cta-card { margin: 0 24px; background: rgba(28,24,20,0.64); border-radius: 18px; padding: 22px; display:flex; align-items:center; justify-content:space-between; cursor:pointer; transition: opacity 0.15s; text-decoration: none; }
  .cta-card:hover { opacity: 0.9; }
`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extractionSummary, setExtractionSummary] = useState(null);
  // "What's next" protocol card — picked by local time + today's
  // completion state. null until protocols + completions have loaded.
  const [nextProtocol, setNextProtocol] = useState(null);
  const [nextProgress, setNextProgress] = useState(null);
  // Server-truth "evening ritual done today" — derived from the same
  // completions fetch as the card pick. The localStorage marker alone
  // (eveningDoneToday) is device-local: on a second device or after a
  // storage clear, Home said "Start your evening reflection" for a
  // ritual the server already knows is finished.
  const [eveningDoneServer, setEveningDoneServer] = useState(false);

  useEffect(() => {
    if (!user) return;
    rciApi
      .snapshot(user.id, token)
      .catch(() => null)
      .then((snap) => setSnapshot(snap))
      .finally(() => setLoading(false));
  }, [user, token]);

  // One-line "Haiven noticed" — today's semantic extraction of the brain
  // dump. Silent (null) on the days the user didn't journal, or before
  // the backend has produced a summary.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    dailyCheckinApi
      .extractionToday(token, localDateKey())
      .then((row) => { if (!cancelled && row?.extraction_summary) setExtractionSummary(row.extraction_summary); })
      .catch(() => { /* 404 = nothing to surface; stay silent */ });
    return () => { cancelled = true; };
  }, [token]);

  // Resolve which protocol to surface on the "Today's protocol" card.
  // Matches the logic used on the Protocol tab so the two stay in sync.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    protocolsApi.list(token).then(async (data) => {
      const protocols = data?.protocols || [];
      if (cancelled || protocols.length === 0) return;
      const today = localDateKey();
      const entries = await Promise.all(
        protocols.map((p) =>
          Promise.all([
            protocolsApi.completions(p.id, today, token).then((d) => (d.completions || []).length).catch(() => 0),
            protocolsApi.today(p.id, today, token).then((detail) => (detail?.steps || []).length).catch(() => p.step_count ?? 0),
          ]).then(([done, total]) => [p.id, done, total])
        )
      );
      if (cancelled) return;
      const countById = Object.fromEntries(entries.map(([id, d]) => [id, d]));
      const totalsById = Object.fromEntries(entries.map(([id, , t]) => [id, t]));
      const pickedId = pickActiveProtocolId(protocols, countById, new Date(), totalsById);
      const picked = protocols.find((p) => p.id === pickedId) || null;
      setNextProtocol(picked);
      setNextProgress(picked ? protocolProgress(picked, countById, new Date(), totalsById) : null);
      setEveningDoneServer(eveningDoneFromServer(totalsById, countById));
    }).catch(() => { /* leave nextProtocol null → card hides */ });
    return () => { cancelled = true; };
  }, [token]);

  const rci = presentedScore(snapshot);
  const totalSnapshots = snapshot?.total_snapshots ?? 0;
  const dayCount = snapshot?.total_snapshots || "—";
  const revealed = isRevealed(snapshot);
  const firstName = user?.full_name?.split(" ")[0] || null;
  const checkedInToday = snapshot?.window_end === localDateKey();
  const checkinHref = checkedInToday ? "/checkin?force=1" : "/checkin";

  const eveningDone = mergeEveningDone(eveningDoneToday(), eveningDoneServer);
  const timeState = homeTimeState();
  const PRIMARY_CARD = selectPrimaryCard({ timeState, checkedInToday, checkinHref, eveningDone });

  if (loading) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="haven" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <span style={{ fontSize: 13, color: PALETTE.textMute, fontWeight: 300 }}>Gathering your signal…</span>
        </div>
      </>
    );
  }

  /* ── SAFETY_GATED: non-directive grounding view ── */
  if (isSafetyGated(snapshot)) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="haven">
          <div style={{
            position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
            width: 420, height: 340, pointerEvents: "none", zIndex: 0,
            background: "radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.09) 0%, transparent 68%)",
          }} />
          <div style={{ position: "relative", zIndex: 1, paddingBottom: "var(--nav-clearance)" }}>
            <div style={{ padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Wordmark />
                <div style={{ fontSize: 13, color: PALETTE.textMute, fontWeight: 300 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
              </div>
            </div>
            <SafetyGatedView
              onCleared={() => {
                // Re-read rather than patching local state: the server
                // is what decides whether the gate is still on, and
                // GET /snapshot applies the stale-gate correction (see
                // app/api/rci.py). A local override here would be a
                // second opinion about the same fact.
                if (!user) return;
                rciApi
                  .snapshot(user.id, token)
                  .catch(() => null)
                  .then((snap) => setSnapshot(snap));
              }}
            />
          </div>
          <BottomNav />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="haven">
        {/* Subtle warm wash */}
        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 340, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.09) 0%, transparent 68%)",
        }} />

        <div style={{ position: "relative", zIndex: 1, paddingBottom: "var(--nav-clearance)" }}>

          {/* Header */}
          <div className="fu d1" style={{ padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Wordmark />
              <div style={{ fontSize: 13, color: PALETTE.textMute, fontWeight: 300 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Signal History entry point (additive — Profile and the
                  Signal screen already link here; Home previously had
                  no entry point at all). A small header icon rather
                  than a new card, so it doesn't compete with the
                  primary daily-action flow below. */}
              <button
                type="button"
                onClick={() => navigate("/history")}
                aria-label="Signal history"
                style={{
                  width: 36, height: 36, borderRadius: "50%", padding: 0,
                  border: `1px solid ${PALETTE.border}`, background: "transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={PALETTE.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3.5 2" />
                </svg>
              </button>
              <div style={{ fontSize: 10, color: PALETTE.primary, border: `1px solid ${PALETTE.border}`, padding: "4px 11px", borderRadius: 99, letterSpacing: "0.07em", fontWeight: 500 }}>Day {dayCount}</div>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: PALETTE.surfaceAlt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: PALETTE.primary, fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>
                {firstName?.[0]?.toUpperCase() || "?"}
              </div>
            </div>
          </div>

          {/* Greeting */}
          <div className="fu d2" style={{ padding: "26px 24px 0" }}>
            <h1 className="serif" style={{ fontSize: 32, lineHeight: 1.15, fontWeight: 400, fontStyle: "italic", letterSpacing: "-0.01em", color: PALETTE.text }}>
              Hello{firstName ? `, ${firstName}` : ""}.
            </h1>
            <p style={{ marginTop: 10, fontSize: 13.5, color: PALETTE.textMid, lineHeight: 1.7, fontWeight: 300, maxWidth: 300 }}>
              {revealed ? "Here's where your signal stands today." : "Your baseline is still forming — keep checking in."}
            </p>
          </div>

          {/* Compact Signal score — taps through to the Signal screen */}
          <div className="fu d3" style={{ margin: "20px 24px 0" }}>
            <button className="lp-signal" onClick={() => navigate("/signal")} aria-label="Open your Signal">
              <div style={{ position: "relative", flexShrink: 0, width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ScoreRing score={revealed ? rci : 0} size={72} />
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span className="serif" style={{ fontSize: 22, fontStyle: revealed ? "normal" : "italic", fontWeight: revealed ? 600 : 400, color: revealed ? PALETTE.text : PALETTE.textFaint, lineHeight: 1 }}>
                    {revealed ? rci : "—"}
                  </span>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9.5, letterSpacing: "0.18em", textTransform: "uppercase", color: PALETTE.primaryMid, fontWeight: 600, marginBottom: 5 }}>
                  {revealed ? "Your Signal today" : "Calibrating"}
                </div>
                {revealed ? (
                  <div style={{ fontSize: 12.5, color: PALETTE.textMid, lineHeight: 1.55, fontWeight: 300 }}>
                    Tap to see how your three systems are reading.
                  </div>
                ) : (
                  <RevealCountdown count={totalSnapshots} total={REVEAL_THRESHOLD} />
                )}
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PALETTE.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto" }}>
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
            {/* Score basis disclosure (Guideline 1.4.1) */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
              <SignalInfoButton />
            </div>
          </div>

          {/* One-line "Haiven noticed" — only when today's extraction exists */}
          {extractionSummary && (
            <div className="fu d3" style={{ margin: "14px 24px 0", padding: "14px 18px", background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.border}`, borderRadius: 14 }}>
              <div style={{ fontSize: 9, color: PALETTE.primary, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, marginBottom: 7 }}>
                Haiven noticed
              </div>
              <p style={{ fontSize: 13.5, color: PALETTE.text, fontWeight: 300, lineHeight: 1.6, fontStyle: "italic" }}>
                {extractionSummary}
              </p>
            </div>
          )}

          {/* Time-aware primary card — the main daily action, swaps by
              local time of day: Morning Check-in / Midday check-in /
              Evening Wind-Down. Moved up (was below ReportCard/
              DeepDiveCard) so the daily action flow — signal today,
              check-in prompt, signal report — isn't pushed below the
              fold by Deep Dive, which now renders last and collapsed. */}
          <div className="fu d3" style={{ margin: "20px 24px 0" }}>
            <div
              onClick={() => navigate(PRIMARY_CARD.href)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(PRIMARY_CARD.href); }}
              style={{
                background: PALETTE.dark, borderRadius: 18, padding: "22px 22px 20px",
                cursor: "pointer", transition: "opacity 0.15s",
              }}
            >
              <div style={{
                fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
                color: PALETTE.primary, fontWeight: 600, marginBottom: 8,
              }}>
                {PRIMARY_CARD.kicker}
              </div>
              <div className="serif" style={{
                fontSize: 24, fontStyle: "italic", color: "#F2EDE3",
                lineHeight: 1.2, marginBottom: 8,
              }}>
                {PRIMARY_CARD.title}
              </div>
              <p style={{
                fontSize: 13, color: "rgba(242,237,227,0.72)", fontWeight: 300,
                lineHeight: 1.55, marginBottom: 18, maxWidth: 300,
              }}>
                {PRIMARY_CARD.sub}
              </p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{
                  fontFamily: "’Playfair Display’, Georgia, serif",
                  fontSize: 16, fontStyle: "italic", color: "#F2EDE3",
                }}>
                  {PRIMARY_CARD.cta}
                </span>
                <div style={{
                  width: 38, height: 38, borderRadius: "50%", background: PALETTE.primary,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#F2EDE3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </div>
              </div>
            </div>
          </div>

          {/* Today's protocol prompt. Suppressed whenever it resolves to
              the same protocol the primary card above already opens —
              rendering both stacked the same action twice (evening from
              5pm, midday from 11am). Moved up together with the primary
              card — the two are one logical "today's actions" unit.
              Morning is sequenced rather than deduped (see
              showsTodaysProtocolCard): Morning Anchor is a genuinely
              separate task from the check-in, so it's held back only
              until check-in is done, not permanently hidden. */}
          {showsTodaysProtocolCard(nextProtocol, PRIMARY_CARD.protocolId, { timeState, checkedInToday }) && (
            <div className="fu d3" style={{ padding: "20px 0 0" }}>
              <div className="cta-card" onClick={() => navigate(`/protocol/${nextProtocol.id}`)} role="button" tabIndex={0}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.primary, fontWeight: 600 }}>Today's protocol</span>
                    {nextProgress?.state === "completed" && (
                      <span style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, padding: "3px 9px", borderRadius: 99, background: "transparent", color: PALETTE.primary, border: `1px solid ${PALETTE.primary}` }}>✓ Completed</span>
                    )}
                    {nextProgress?.state === "missed" && (
                      <span style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, padding: "3px 9px", borderRadius: 99, background: "transparent", color: "#9E5A4A", border: "1px solid #9E5A4A" }}>
                        {nextProgress.done > 0 ? `Missed · ${nextProgress.done} of ${nextProgress.total}` : "Missed"}
                      </span>
                    )}
                  </div>
                  <div className="serif" style={{ fontSize: 19, color: "#F2EDE3", fontStyle: "italic", lineHeight: 1.35 }}>{nextProtocol.name}</div>
                  {nextProgress?.state === "in_progress" && nextProgress.total > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ flex: 1, height: 4, borderRadius: 999, background: "rgba(242, 237, 227, 0.18)", overflow: "hidden" }}>
                        <div style={{ width: `${(nextProgress.done / nextProgress.total) * 100}%`, height: "100%", background: "#9E8456", borderRadius: 999, transition: "width 200ms ease" }} />
                      </div>
                      <span style={{ fontSize: 10.5, color: "rgba(242, 237, 227, 0.7)", fontWeight: 500, letterSpacing: "0.02em", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{`${nextProgress.done} of ${nextProgress.total}`}</span>
                    </div>
                  )}
                </div>
                <div style={{ width: 42, height: 42, borderRadius: "50%", background: PALETTE.primary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 12 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#F2EDE3" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </div>
              </div>
            </div>
          )}

          {/* Push opt-in card (opens the pre-prompt explainer). The
              component gates itself: >=3 check-ins, capable platform,
              not previously dismissed. It was built for this spot but
              was never mounted — golf-cohort fix (Jul 2026). */}
          <div style={{ margin: "14px 24px 0" }}>
            <PushNotificationPrompt totalCheckins={totalSnapshots} />
          </div>

          {/* "From your practitioner" — renders ONLY when a consented
              active practitioner protocol (or a pending consent ask)
              exists; otherwise invisible and Home is unchanged. NOT the
              Protocol tab — that word means the daily rituals. */}
          <div className="fu d3" style={{ margin: "14px 24px 0" }}>
            <ProtocolCard />
          </div>

          {/* Signal Report entry point — the discovery fix for the share
              flywheel. Component self-gates: nothing at 0 check-ins,
              a quiet "unlocks after N more" line below the ≥3 summary
              threshold, the /report preview entry above it. Pre-session
              copy swap when a care-team session is imminent. */}
          <div className="fu d3" style={{ margin: "14px 24px 0" }}>
            <ReportCard totalSnapshots={totalSnapshots} user={user} />
          </div>

          {/* Deep Dive entry point (moved from Profile — the Profile
              placement wasn't discoverable). Rendered LAST and
              collapsed by default (collapsible) — it was previously
              sitting between ReportCard and the primary card, fully
              expanded, pushing the whole daily-action flow below the
              fold. Fully self-contained: fetches its own state and
              self-gates on check-in count (locked / teaser / unlocked /
              generated), same as before; collapsing/expanding doesn't
              change any of that gating. */}
          <div className="fu d3" style={{ margin: "14px 24px 0" }}>
            <DeepDiveCard collapsible />
          </div>

        </div>

        <BottomNav />
      </div>
    </>
  );
}
